import { z } from 'zod';

/**
 * Схема валидации для создания/редактирования категории цен
 */
export const priceCategorySchema = z.object({
  name: z
    .string()
    .min(1, 'Название категории обязательно для заполнения')
    .min(2, 'Название категории должно содержать минимум 2 символа')
    .max(100, 'Название категории не должно превышать 100 символов'),
  sortOrder: z
    .number()
    .int('Порядок сортировки должен быть целым числом')
    .min(0, 'Порядок сортировки не может быть отрицательным')
    .default(0),
  isActive: z.boolean().default(true)
});

/**
 * Тип данных формы категории цен
 */
export type PriceCategoryFormData = z.infer<typeof priceCategorySchema>;

/**
 * Схема валидации для создания/редактирования услуги (форма)
 * Принимает числовые значения для цен и преобразует их в строки
 */
export const priceItemSchema = z
  .object({
    categoryId: z
      .number()
      .int('ID категории должен быть целым числом')
      .min(1, 'Выберите категорию'),
    doctorId: z
      .number()
      .int('ID врача должен быть целым числом')
      .min(1, 'ID врача должен быть положительным числом')
      .optional()
      .nullable()
      .transform((val) => {
        // Handle NaN values by converting them to null
        if (typeof val === 'number' && isNaN(val)) {
          return null;
        }
        return val;
      }),
    name: z
      .string()
      .min(1, 'Название услуги обязательно для заполнения')
      .min(2, 'Название услуги должно содержать минимум 2 символа')
      .max(200, 'Название услуги не должно превышать 200 символов'),
    /**
     * Описание услуги (опциональное поле)
     * Подробная информация о медицинской услуге, особенностях проведения
     * Максимальная длина: 1000 символов
     */
    description: z
      .string()
      .max(1000, 'Описание услуги не должно превышать 1000 символов')
      .optional()
      .nullable(),
    priceOffline: z.string().optional(),
    priceOnline: z.string().optional(),
    meta: z.record(z.string(), z.any()).optional().nullable(),
    isActive: z.boolean().default(true),
    sortOrder: z
      .number()
      .int('Порядок сортировки должен быть целым числом')
      .min(0, 'Порядок сортировки не может быть отрицательным')
      .default(0)
  })
  .refine((data) => data.priceOffline !== null || data.priceOnline !== null, {
    message: 'Укажите хотя бы одну цену (очную или онлайн)',
    path: ['priceOffline']
  });

/**
 * Тип данных формы услуги (до трансформации)
 * Используется для типизации react-hook-form
 * Основан на Prisma PriceItemCreateInput с адаптацией для форм
 */
export type PriceItemFormData = {
  /** ID категории услуги */
  categoryId: number;
  /** ID врача (если услуга привязана к конкретному врачу) */
  doctorId?: number | null;
  /** Название услуги */
  name: string;
  /** Описание услуги (опциональное, до 1000 символов) */
  description?: string | null;
  /** Цена очной консультации */
  priceOffline?: string | null;
  /** Цена онлайн консультации */
  priceOnline?: string | null;
  /** Дополнительные метаданные в формате ключ-значение */
  meta?: Record<string, any> | null;
  /** Статус активности услуги */
  isActive: boolean;
  /** Порядок сортировки */
  sortOrder: number;
};
